import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { PRODUCTS } from "@/data/products";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Uploaded assets are stored as relative "/api/files/..." paths so the DB stays
// portable across domains; resolve them against the backend URL at render time.
export const assetUrl = (p) => (p && p.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p);

export const TILE_ORDER = ["Tapware", "Bathtubs", "Basins", "LED Mirrors", "Vanities"];

export const DEFAULT_SITE = {
  heroMedia: "/hero.mp4",
  heroMediaType: "video",
  heroEyebrow: "Architectural Tapware & Bathroom Fixtures",
  heroLine1: "WHERE FORM",
  heroLine2: "MEETS FLOW.",
  heroSub:
    "Tapware, showers, baths, basins and LED mirrors — precision-engineered for everyday performance, designed for the modern Australian home.",
  tiles: {
    Tapware: "https://images.pexels.com/photos/4194987/pexels-photo-4194987.jpeg?auto=compress&cs=tinysrgb&w=1260&h=900&dpr=1",
    Bathtubs: "https://images.unsplash.com/photo-1507652313519-d4e9174996dd?q=80&w=1260&auto=format&fit=crop",
    Basins: "https://images.unsplash.com/photo-1620626011761-996317b8d101?q=80&w=1260&auto=format&fit=crop",
    "LED Mirrors": "https://images.pexels.com/photos/29997237/pexels-photo-29997237.jpeg?auto=compress&cs=tinysrgb&w=1260&h=900&dpr=1",
    Vanities: "https://images.pexels.com/photos/10222468/pexels-photo-10222468.jpeg?auto=compress&cs=tinysrgb&w=1600&h=700&dpr=1",
  },
  featuredIds: ["orbit-basin-tap", "serenique-classic-freestanding-bath", "arch-led-mirror", "orbit-rail-shower-overhead"],
};

export const mergeProduct = (p, ov) => {
  if (!ov) return p;
  const m = { ...p };
  if (ov.name) m.name = ov.name;
  if (ov.sku) m.sku = ov.sku;
  if (ov.blurb) m.blurb = ov.blurb;
  if (ov.extraSpecs) m.extraSpecs = ov.extraSpecs;
  if (ov.specPdf) {
    m.specPdf = ov.specPdf;
    if (ov.specPdfSize) m.specPdfSize = ov.specPdfSize;
  }
  if (ov.specPdfs) {
    m.specPdfs = {
      ...(p.specPdfs || {}),
      ...Object.fromEntries(Object.entries(ov.specPdfs).filter(([, v]) => v && v.url)),
    };
  }
  const img = ov.images;
  if (img) {
    if (img.image) m.image = img.image;
    if (img.techImage) m.techImage = img.techImage;
    if (img.galleryImages) m.galleryImages = img.galleryImages;
    if (img.finishImages) {
      const fi = { ...(p.finishImages || {}) };
      Object.entries(img.finishImages).forEach(([k, v]) => (v ? (fi[k] = v) : delete fi[k]));
      m.finishImages = fi;
    }
    if (img.finishGalleries) {
      const fg = { ...(p.finishGalleries || {}) };
      Object.entries(img.finishGalleries).forEach(([k, v]) => (v && v.length ? (fg[k] = v) : delete fg[k]));
      m.finishGalleries = fg;
    }
  }
  return m;
};

const SiteCtx = createContext({ site: DEFAULT_SITE, products: PRODUCTS, overrides: {}, refresh: () => {} });

export const SiteContentProvider = ({ children }) => {
  const [site, setSite] = useState(DEFAULT_SITE);
  const [overrides, setOverrides] = useState({});

  const load = () => {
    axios
      .get(`${API}/content/site`)
      .then(({ data }) => {
        if (!data) return;
        const clean = Object.fromEntries(
          Object.entries(data).filter(([k, v]) => v != null && k !== "updated_at" && k !== "updated_by")
        );
        setSite((s) => ({ ...s, ...clean, tiles: { ...s.tiles, ...(clean.tiles || {}) } }));
      })
      .catch(() => {});
    axios
      .get(`${API}/content/products`)
      .then(({ data }) => setOverrides(data || {}))
      .catch(() => {});
  };

  useEffect(load, []);

  const products = useMemo(() => PRODUCTS.map((p) => mergeProduct(p, overrides[p.id])), [overrides]);

  return <SiteCtx.Provider value={{ site, products, overrides, refresh: load }}>{children}</SiteCtx.Provider>;
};

export const useSite = () => useContext(SiteCtx);
